import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { deleteMember, getMemberDeletionStatus, type MemberResponse } from "@/api/members";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/design-system/dialogs/ConfirmDialog";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";

export function MemberDeletion({ member }: { member: MemberResponse }) {
  const { permissions } = useEffectivePermissions();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const deleting = useRef(false);
  const authorized = permissions.has("MEMBERS_MANAGE") && permissions.has("SETTINGS_MANAGE");
  const safety = useQuery({
    queryKey: ["member-deletion-status", member.church_id, member.id],
    queryFn: () => getMemberDeletionStatus(member.church_id, member.id),
    enabled: authorized,
  });
  if (!authorized) return null;

  async function remove() {
    if (deleting.current) return;
    deleting.current = true;
    setPending(true);
    setError("");
    try {
      await deleteMember(member.church_id, member.id);
      setConfirming(false);
      navigate("/members", { replace: true, state: { memberDeleted: true } });
      client.removeQueries({ queryKey: ["member", member.church_id, member.id], exact: true });
      // Member removal changes directory/search results, household summaries,
      // check-in candidates and reporting denominators across this church.
      void client.invalidateQueries({ predicate: query => query.queryKey[1] === member.church_id });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to delete member. Please try again.");
      setConfirming(false);
      void safety.refetch();
    } finally {
      deleting.current = false;
      setPending(false);
    }
  }

  return <section className="mt-6 space-y-3 rounded-2xl border border-rose-500/20 bg-rose-500/5 p-4 sm:p-5" aria-label="Member deletion">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h2 className="font-semibold text-white">Permanent deletion</h2>
        <p className="mt-1 text-sm text-slate-400">Remove a member created by accident only when they have no protected history or access dependencies.</p>
      </div>
      <Button variant="danger" className="shrink-0" disabled={!safety.data?.can_delete || safety.isError || safety.isFetching || pending}
        aria-describedby="member-delete-explanation" onClick={() => { setError(""); setConfirming(true); }}>
        <Trash2 className="mr-2 inline h-4 w-4" />Delete Member
      </Button>
    </div>
    <div id="member-delete-explanation" className="space-y-1 text-sm text-slate-300" aria-live="polite">
      {safety.isFetching ? <p>Checking whether this member can be deleted...</p> : safety.isError ?
        <p>Unable to check deletion safety. <button className="underline" onClick={() => void safety.refetch()}>Retry</button></p> :
        safety.data && <>
          {safety.data.reasons.map(reason => <p key={reason}>{reason}</p>)}
          {!safety.data.can_delete && <p>{member.status === "INACTIVE" ? "This member is already inactive; their history is retained." : "To retain their history, use Edit, then set Status to Inactive."} Application access is managed separately.</p>}
          {safety.data.can_delete && safety.data.household_notice && <p>{safety.data.household_notice}</p>}
        </>}
    </div>
    {error && <p role="alert" className="text-sm text-rose-300">{error}</p>}
    <ConfirmDialog open={confirming} title="Permanently delete member?"
      description={`Delete ${member.first_name} ${member.last_name}? This cannot be undone. ${safety.data?.household_notice ?? ""} The server will check dependencies again before deleting.`}
      destructive confirmText="Permanently Delete" pending={pending}
      onCancel={() => { if (!deleting.current) setConfirming(false); }} onConfirm={() => void remove()} />
  </section>;
}
